import React, { useEffect, useState } from 'react';
import api from '../api';
import { PageHeader, Modal, Badge, EmptyState, useToast, Skeleton } from '../ui';
import PhotoInput from '../PhotoInput';
import { useLang } from '../Layout';
import { FaBoxOpen, FaBoxesStacked, FaPlus } from 'react-icons/fa6';

const CATEGORIES = [
  { key: '', labelBn: 'সব', labelEn: 'All', color: 'default' },
  { key: 'structure', labelBn: 'স্ট্রাকচার', labelEn: 'Structure', color: 'slate' },
  { key: 'plumbing', labelBn: 'পাইপ/পানি', labelEn: 'Plumbing', color: 'sky' },
  { key: 'electric', labelBn: 'ইলেকট্রিক', labelEn: 'Electric', color: 'amber' },
  { key: 'finishing', labelBn: 'টাইলস/ফিনিশিং', labelEn: 'Finishing', color: 'rose' },
  { key: 'tools', labelBn: 'টুলস', labelEn: 'Tools', color: 'emerald' },
  { key: 'other', labelBn: 'অন্যান্য', labelEn: 'Other', color: 'ink' },
];
const catLabel = (key, lang) => (CATEGORIES.find((c) => c.key === key) || CATEGORIES[0])[lang === 'bn' ? 'labelBn' : 'labelEn'];
const CAT_COLOR = { structure: 'bg-slate-100 text-slate-700', plumbing: 'bg-sky-50 text-sky-700', electric: 'bg-amber-50 text-amber-700', finishing: 'bg-rose-50 text-rose-700', tools: 'bg-emerald-50 text-emerald-700', other: 'bg-ink-100 text-ink-600', '': 'bg-ink-50 text-ink-400' };

const T = {
  bn: {
    title: 'মাল স্টক', subtitle: 'প্রতিটি কন্ট্রাক্টরের জন্য আলাদা মাল — যেমন প্লাম্বারের জন্য পাইপ',
    stockIn: 'মাল ঢুকেছে', stockOut: 'মাল বেরিয়েছে', inventory: 'স্টক তালিকা',
    contractors: 'কন্ট্রাক্টর অনুযায়ী', materials: 'মালের তালিকা', newMaterial: 'নতুন মাল',
    inTotal: 'ঢুকেছে', outTotal: 'বেরিয়েছে', empty: 'খালি', balance: 'ব্যালেন্স',
    material: 'মাল', qty: 'পরিমাণ', unit: 'একক', supplier: 'সাপ্লায়ার', challan: 'চালান নম্বর',
    amount: 'মূল্য (৳)', takenBy: 'মাল নিয়েছে (কন্ট্রাক্টর)', stockTo: 'কোন কন্ট্রাক্টরের জন্য কেনা হয়েছে',
    general: '— কোম্পানির স্টক —', selectSide: '— কন্ট্রাক্টর নির্বাচন —', select: '— নির্বাচন করুন —',
    workDesc: 'কী কাজে লাগলো', workPlaceholder: 'যেমন: ২য় তলার বাথরুম', worker: 'কর্মী (ঐচ্ছিক)',
    commit: 'মাল দায়বদ্ধ করুন', addStock: 'স্টক যোগ করুন', add: 'যোগ করুন',
    nameHeader: 'নাম', balanceHeader: 'ব্যালেন্স', emptyMaterials: 'কোনো মাল নেই। নতুন মাল যোগ করুন।',
    emptySide: 'এই কন্ট্রাক্টরের জন্য এখনো কোনো মাল নেই।', category: 'ক্যাটাগরি',
    inHint: 'মাল কেনা হয় মূলত কোনো কন্ট্রাক্টরের জন্যই — যেমন প্লাম্বারের জন্য পাইপ।',
  },
  en: {
    title: 'Material Stock', subtitle: 'Separate stock for every contractor — e.g. pipe for the plumber',
    stockIn: 'Stock In', stockOut: 'Stock Out', all: 'Stock List', contractors: 'By Contractor',
    materials: 'Material List', newMaterial: 'New Material', inTotal: 'In', outTotal: 'Out',
    empty: 'Empty', balance: 'Balance', material: 'Material', qty: 'Quantity', unit: 'Unit',
    supplier: 'Supplier', challan: 'Challan No.', amount: 'Amount (৳)', takenBy: 'Taken By (Contractor)',
    stockTo: 'Bought for which contractor',
    general: '— Company Stock —', selectSide: '— Select Contractor —', selectWork: '— Select —',
    workDesc: 'Work Description', workPlaceholder: 'e.g. 2nd floor bathroom', worker: 'Worker (optional)',
    commit: 'Commit Stock', addStock: 'Add Stock', photoName: 'Photo', name: 'Name',
    balanceHeader: 'Balance', emptyMaterials: 'No materials yet. Add one.', emptySide: 'No stock for this contractor yet.',
    category: 'Category',
    inB: 'Materials are bought for a specific contractor — e.g. pipes for the plumber.',
  },
};

export default function Stock({ role }) {
  const { lang } = useLang(); const t = T[lang];
  const isManager = role === 'manager';
  const base = isManager ? '/manager' : '/owner';
  const matBase = isManager ? '/manager/materials' : '/owner/stock/materials';
  const { toast } = useToast();

  const [tab, setTab] = useState('all'); // all | side | materials
  const [materials, setMaterials] = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [contractors, setContractors] = useState([]);
  const [sideRows, setSideRows] = useState([]);
  const [sideId, setSideId] = useState('');
  const [sideWorkers, setSideWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIn, setShowIn] = useState(false);
  const [showOut, setShowOut] = useState(false);
  const [showMat, setShowMat] = useState(false);
  const [err, setErr] = useState('');

  const defaultInForm = { material_id: '', qty: '', contractor_id: '', supplier: '', challan_no: '', amount: '', photo: null };
  const [inForm, setInForm] = useState(defaultInForm);
  const [outForm, setOutForm] = useState({ material_id: '', qty: '', contractor_id: '', worker_id: '', work_description: '', photo: null });
  const [matForm, setMatForm] = useState({ name: '', unit: 'piece', category: '' });

  const loadAll = () => {
    api.get(matBase).then(({ data }) => setMaterials(data.materials)).catch(() => {});
    api.get(`${base}/contractors`).then(({ data }) => setContractors(data.contractors || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); setLoading(false); }, []);

  useEffect(() => {
    if (sideId) {
      api.get(`${base}/stock/by-contractor?contractor_id=${sideId}`).then(({ data }) => setSideRows(data.materials || [])).catch(() => {});
    } else { setSideRows([]); }
  }, [sideId]);

  // Workers of the selected contractor (for stock-out attribution)
  useEffect(() => {
    if (outForm.contractor_id) {
      api.get(`${base}/workers?contractor_id=${outForm.contractor_id}`).then(({ data }) => setSideWorkers(data.workers || [])).catch(() => {});
    } else { setSideWorkers([]); }
  }, [outForm.contractor_id]);

  async function stockIn(e) {
    e.preventDefault(); setErr('');
    try {
      const resp = await api.post(`${base}/stock/in`, inForm);
      toast(resp.data.message || t.addStock, 'success');
      setShowIn(false);
      setInForm(defaultInForm);
      loadAll();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }

  async function stockOut(e) {
    e.preventDefault(); setErr('');
    try {
      const resp = await api.post(`${base}/stock/out`, outForm);
      toast(resp.data.message, 'success');
      setShowOut(false);
      setOutForm({ material_id: '', qty: '', contractor_id: '', worker_id: '', work_description: '', photo: null });
      loadAll();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }

  async function addMat(e) {
    e.preventDefault(); setErr('');
    try {
      const resp = await api.post(matBase, matForm);
      toast(resp.data.message || 'নতুন মাল যোগ হয়েছে', 'success');
      setShowMat(false); setMatForm({ name: '', unit: 'piece', category: '' }); loadAll();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }

  return (
    <div className="space-y-4">
      <PageHeader title={t.title} subtitle={t.subtitle}
        actions={
          <>
            <button className="btn-ghost text-sm" onClick={() => { setShowIn(true); setErr(''); }}><FaPlus /> {t.stockIn}</button>
            <button className="btn-primary text-sm" onClick={() => { setShowOut(true); setErr(''); }}><FaPlus /> {t.stockOut}</button>
          </>
        } />

      {/* Tabs */}
      <div className="flex gap-1.5 bg-white border border-ink-100 rounded-xl p-1 w-fit">
        {[['all', lang === 'bn' ? 'স্টক তালিকা' : 'Stock List'], ['side', t.contractors], ['materials', t.materials]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${tab === k ? 'bg-ink-900 text-white' : 'text-ink-500 hover:bg-ink-50'}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="grid gap-3"><Skeleton className="h-24" /></div> : null}

      {/* ── All stock balances ── */}
      {tab === 'all' && (
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
          <table className="charm-table stack-sm">
            <thead><tr><th>{t.material}</th><th>{t.inTotal}</th><th>{t.outTotal}</th><th>{t.balance}</th></tr></thead>
            <tbody>
              {materials.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center text-sm"><FaBoxOpen /></div>
                      <span className="font-semibold">{m.name}</span>
                    </div>
                  </td>
                  <td className="text-ink-500">{m.total_in}</td>
                  <td className="text-ink-500">{m.total_out}</td>
                  <td>
                    <span className={`font-bold inline-flex items-center gap-1.5 ${m.balance <= 0 ? 'text-rose-600' : 'text-ink-900'}`}>
                      {m.balance} <span className="text-xs font-normal text-ink-400">{m.unit}</span>
                      {m.balance <= 0 && <Badge color="absent">{t.empty}</Badge>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {materials.length === 0 && <EmptyState icon="📦" title={t.emptyMaterials} />}
        </div>
      )}

      {/* ── Per-side stock ── */}
      {tab === 'side' && (
        <div className="space-y-4">
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setSideId('')}
              className={`btn text-xs whitespace-nowrap ${!sideId ? 'bg-ink-900 text-white' : 'bg-white text-ink-500 border border-ink-200'}`}>{lang === 'bn' ? 'সব কন্ট্রাক্টর' : 'All Contractors'}</button>
            {contractors.map((c) => (
              <button key={c.id} onClick={() => setSideId(String(c.id))}
                className={`btn text-xs whitespace-nowrap ${sideId === String(c.id) ? 'bg-brand-500 text-white' : 'bg-white text-ink-500 border border-ink-200'}`}>
                {c.name} <span className="opacity-60">({c.trade})</span>
              </button>
            ))}
          </div>

          {sideId ? (
            <div className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
              <table className="overflow-x-auto w-full text-sm">
                <thead><tr className="bg-ink-50/50"><th className="text-left text-xs font-semibold text-ink-400 uppercase px-4 py-3">{t.material}</th><th className="text-right px-4 py-3">{t.inTotal}</th><th className="text-right px-4 py-3">{t.outTotal}</th><th className="text-right px-4 py-3">{t.balance}</th></tr></thead>
                <tbody>
                  {sideRows.map((r) => (
                    <tr key={r.material_id} className="border-t border-ink-100">
                      <td className="px-4 py-3 font-semibold">{r.material_name}</td>
                      <td className="px-4 py-3 text-right text-ink-500">{r.in_qty} {r.unit}</td>
                      <td className="px-4 py-3 text-right text-ink-500">{r.out_qty} {r.unit}</td>
                      <td className={`px-4 py-3 text-right font-bold ${r.balance <= 0 ? 'text-rose-600' : 'text-ink-900'}`}>{r.balance} {r.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sideRows.length === 0 && <EmptyState icon="🏗️" title={t.emptySide} />}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {contractors.map((c) => (
                <button key={c.id} onClick={() => setSideId(String(c.id))}
                  className="bg-white rounded-xl border border-ink-100 shadow-soft p-4 text-left hover:border-brand-300 hover:shadow-pop transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><FaBoxesStacked /></span>
                      <div>
                        <div className="font-semibold text-ink-900">{c.name}</div>
                        <div className="text-xs text-ink-400">{c.trade} • {c.worker_count} জন</div>
                      </div>
                    </div>
                    <span className="text-ink-300">→</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Materials catalog ── */}
      {tab === 'materials' && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              {CATEGORIES.map((c) => (
                <button key={c.key} onClick={() => setCatFilter(c.key)}
                  className={`btn text-xs whitespace-nowrap ${catFilter === c.key ? 'bg-ink-900 text-white' : 'bg-white text-ink-500 border border-ink-200'}`}>
                  {c[lang === 'bn' ? 'labelBn' : 'labelEn']}
                </button>
              ))}
            </div>
            <button className="btn-ghost text-sm flex items-center gap-2 shrink-0" onClick={() => { setShowMat(true); setErr(''); }}><FaPlus /> {t.newMaterial}</button>
          </div>
          <div className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
            <table className="overflow-x-auto w-full text-sm">
              <thead><tr className="bg-ink-50/60"><th className="text-left text-xs font-semibold text-ink-400 uppercase px-4 py-2">{t.nameHeader}</th><th className="text-left px-4 py-2">{t.category}</th><th className="text-left px-4 py-2">{t.unit}</th><th className="text-right px-4 py-2">{t.balanceHeader}</th></tr></thead>
              <tbody>
                {materials.filter((m) => !catFilter || m.category === catFilter).map((m) => (
                  <tr key={m.id} className="border-t border-ink-100">
                    <td className="px-4 py-3 font-semibold">{m.name}</td>
                    <td className="px-4 py-3">
                      {m.category
                        ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${CAT_COLOR[m.category] || CAT_COLOR.other}`}>{catLabel(m.category, lang)}</span>
                        : <span className="text-xs text-ink-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-500">{m.unit}</td>
                    <td className="px-4 py-3 text-right font-bold text-ink-900">{m.balance} {m.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {materials.length === 0 && <EmptyState icon="🧱" title={t.emptyMaterials} />}
          </div>
        </div>
      )}

      {/* Stock In modal */}
      <Modal open={showIn} onClose={() => setShowIn(false)} title={t.stockIn}>
        <form onSubmit={stockIn} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div>
            <label className="label">{t.material} <span className="text-rose-500">*</span></label>
            <select className="input" value={inForm.material_id} onChange={(e) => setInForm({ ...inForm, material_id: e.target.value })} required>
              <option value="">{t.select}</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t.qty} <span className="text-rose-500">*</span></label>
              <input className="input" type="number" min="0" step="any" value={inForm.qty} onChange={(e) => setInForm({ ...inForm, qty: e.target.value })} required />
            </div>
            <div>
              <label className="label">{t.unit}</label>
              <select className="input" value={inForm.unit} onChange={(e) => setInForm({ ...inForm, unit: e.target.value })}>
                <option value="">অটো</option>
                {['piece', 'kg', 'cft', 'bag', 'ft', 'liter', 'box', 'dozen', 'set', 'other'].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">{t.stockTo} <span className="text-rose-500">*</span></label>
            <select className="input" value={inForm.contractor_id} onChange={(e) => setInForm({ ...inForm, contractor_id: e.target.value })} required>
              <option value="">{t.selectSide}</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.trade})</option>)}
            </select>
            <p className="text-xs text-ink-400 mt-1">{t.inHint}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">{t.supplier}</label><input className="input" value={inForm.supplier} onChange={(e) => setInForm({ ...inForm, supplier: e.target.value })} /></div>
            <div><label className="label">{t.challan}</label><input className="input" value={inForm.challan_no} onChange={(e) => setInForm({ ...inForm, challan_no: e.target.value })} /></div>
          </div>
          <div><label className="label">{t.amount}</label><input className="input" type="number" min="0" value={inForm.amount} onChange={(e) => setInForm({ ...inForm, amount: e.target.value })} /></div>
          <PhotoInput value={inForm.photo} onChange={(p) => setInForm({ ...inForm, photo: p })} label="চালানের ছবি" hint="ঐচ্ছিক" />
          <button className="btn-primary w-full py-3">{t.addStock}</button>
        </form>
      </Modal>

      {/* Stock Out modal */}
      <Modal open={showOut} onClose={() => setShowOut(false)} title={t.stockOut}>
        <form onSubmit={stockOut} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div>
            <label className="label">{t.material} <span className="text-rose-500">*</span></label>
            <select className="input" value={outForm.material_id} onChange={(e) => setOutForm({ ...outForm, material_id: e.target.value })} required>
              <option value="">{t.select}</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t.qty} <span className="text-rose-500">*</span></label>
              <input className="input" type="number" min="0" step="any" value={outForm.qty} onChange={(e) => setOutForm({ ...outForm, qty: e.target.value })} required />
            </div>
            <div>
              <label className="label">{t.unit}</label>
              <select className="input" value={outForm.unit} onChange={(e) => setOutForm({ ...outForm, unit: e.target.value })}>
                <option value="">অটো</option>
                {['piece', 'kg', 'cft', 'bag', 'ft', 'liter', 'box', 'dozen', 'set', 'other'].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">{t.takenBy} <span className="text-rose-500">*</span></label>
            <select className="input" value={outForm.contractor_id} onChange={(e) => { setOutForm({ ...outForm, contractor_id: e.target.value, worker_id: '' }); }} required>
              <option value="">{t.selectSide}</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.trade})</option>)}
            </select>
          </div>
          {outForm.contractor_id && (
            <div>
              <label className="label">{t.worker}</label>
              <select className="input" value={outForm.worker_id} onChange={(e) => setOutForm({ ...outForm, worker_id: e.target.value })}>
                <option value="">— {t.selectWork} —</option>
                {sideWorkers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          )}
          <div><label className="label">{t.workDesc}</label><input className="input" placeholder={t.workPlaceholder} value={outForm.work_description} onChange={(e) => setOutForm({ ...outForm, work_description: e.target.value })} /></div>
          <PhotoInput value={outForm.photo} onChange={(p) => setOutForm({ ...outForm, photo: p })} label="মালের ছবি" hint="ঐচ্ছিক" />
          <button className="btn-primary w-full py-3">{t.stockOut}</button>
        </form>
      </Modal>

      {/* New material modal */}
      <Modal open={showMat} onClose={() => setShowMat(false)} title={t.newMaterial}>
        <form onSubmit={addMat} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div>
            <label className="label">{t.material} <span className="text-rose-500">*</span></label>
            <input className="input" placeholder="যেমন: সিমেন্ট, পাইপ, রড" value={matForm.name} onChange={(e) => setMatForm({ ...matForm, name: e.target.value })} required />
          </div>
          <div>
            <label className="label">{t.unit}</label>
            <select className="input" value={matForm.unit} onChange={(e) => setMatForm({ ...matForm, unit: e.target.value })}>
              {['piece', 'kg', 'cft', 'bag', 'ft', 'liter', 'box', 'dozen', 'set', 'other'].map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t.category}</label>
            <select className="input" value={matForm.category} onChange={(e) => setMatForm({ ...matForm, category: e.target.value })}>
              {CATEGORIES.slice(1).map((c) => <option key={c.key} value={c.key}>{c[lang === 'bn' ? 'labelBn' : 'labelEn']}</option>)}
            </select>
            <p className="text-xs text-ink-400 mt-1">{lang === 'bn' ? 'একই মাল একাধিক সাইড ব্যবহার করতে পারবে (যেমন পাইপ — প্লাম্বার ও ইলেকট্রিক)' : 'One material can be used by multiple sides (e.g. pipe — plumber & electric)'}</p>
          </div>
          <button className="btn-primary w-full py-3">{t.newMaterial}</button>
        </form>
      </Modal>
    </div>
  );
}